import {
  doc,
  collection,
  addDoc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  writeBatch,
  onSnapshot,
  serverTimestamp,
  arrayRemove,
  arrayUnion,
  deleteField,
  query,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { CHATTERS_ID, MAX_PLAYERS } from '../../constants/game';

function generateCode() {
  // Exclude visually ambiguous (L/1/I/0/O/S/5) and phonetically ambiguous
  // when spoken aloud (T/D, M/N, V/B, F/X) characters.
  const chars = 'ACEGHJKPQRUWYZ2346789';
  const array = new Uint8Array(4);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
}

export async function createGame({ hostId, hostDisplayName, chattersEnabled = false, inPersonMode = false, rules = { noRepeatArtist: false, noRepeatSong: false } }) {
  const code = generateCode();
  const playerIds = [hostId];
  const playerNames = { [hostId]: hostDisplayName };

  // Generate a separate chatter code so stream audiences use a different code
  // from regular players, preventing accidental role confusion.
  let chatterCode = null;
  if (chattersEnabled) {
    chatterCode = generateCode();
    // Regenerate until it differs from the player code (collision is rare but possible)
    while (chatterCode === code) chatterCode = generateCode();
    playerIds.push(CHATTERS_ID);
    playerNames[CHATTERS_ID] = 'Chatters';
  }

  const ref = await addDoc(collection(db, 'games'), {
    hostId,
    code,
    ...(chatterCode && { chatterCode }),
    chattersEnabled,
    inPersonMode,
    rules,
    status: 'waiting',
    playerIds,
    playerNames,
    activeRoundId: null,
    roundCount: 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function joinGameByCode(code, userId, displayName) {
  const waitingQ = query(
    collection(db, 'games'),
    where('code', '==', code.toUpperCase()),
    where('status', '==', 'waiting')
  );
  const waitingSnap = await getDocs(waitingQ);

  if (!waitingSnap.empty) {
    const gameDoc = waitingSnap.docs[0];
    const game = gameDoc.data();
    if (game.playerIds.includes(userId)) return gameDoc.id;
    if (game.locked) throw new Error('This lobby is locked by the host.');
    const regularPlayerCount = game.playerIds.filter(id => id !== CHATTERS_ID).length;
    if (regularPlayerCount >= MAX_PLAYERS) {
      throw new Error(`This game is full (maximum ${MAX_PLAYERS} players).`);
    }
    await updateDoc(gameDoc.ref, {
      playerIds: [...game.playerIds, userId],
      [`playerNames.${userId}`]: displayName,
    });
    return gameDoc.id;
  }

  // Not found as waiting — look up regardless of status
  const anyQ = query(
    collection(db, 'games'),
    where('code', '==', code.toUpperCase())
  );
  const anySnap = await getDocs(anyQ);

  if (anySnap.empty) throw new Error('Game not found or already started.');

  const gameDoc = anySnap.docs[0];
  const game = gameDoc.data();

  // Already in the game (e.g. page refresh, or rejoining after leaving)
  if (game.playerIds.includes(userId)) return gameDoc.id;

  // Allow joining or rejoining an active game mid-game
  if (game.status === 'active') {
    if (game.locked) throw new Error('This lobby is locked by the host.');
    const regularPlayerCount = game.playerIds.filter(id => id !== CHATTERS_ID).length;
    if (regularPlayerCount >= MAX_PLAYERS) {
      throw new Error(`This game is full (maximum ${MAX_PLAYERS} players).`);
    }
    await updateDoc(gameDoc.ref, {
      playerIds: [...game.playerIds, userId],
      [`playerNames.${userId}`]: displayName,
    });
    return gameDoc.id;
  }

  throw new Error('This game has already ended.');
}

export async function toggleLobbyLock(gameId, locked) {
  await updateDoc(doc(db, 'games', gameId), { locked });
}

export async function updateGameRules(gameId, rules) {
  await updateDoc(doc(db, 'games', gameId), { rules });
}

/**
 * Host kicks a player from the game.
 * Adds the player's UID to kickedPlayerIds; they cannot rejoin or write scores.
 */
export async function kickPlayer(gameId, uid) {
  await updateDoc(doc(db, 'games', gameId), {
    kickedPlayerIds: arrayUnion(uid),
    playerIds: arrayRemove(uid),
    [`playerNames.${uid}`]: deleteField(),
  });
}

export async function startGame(gameId) {
  await updateDoc(doc(db, 'games', gameId), { status: 'active' });
}

export async function startRound(gameId, { chooserId, song = null, currentRoundCount }) {
  const roundNumber = currentRoundCount + 1;
  const roundRef = doc(collection(db, 'games', gameId, 'rounds'));

  const batch = writeBatch(db);
  batch.set(roundRef, {
    roundNumber,
    chooserId,
    song, // { title, artist } or null during song_selection
    phase: song ? 'scoring' : 'song_selection',
    predRevealOrder: [],
    scoreRevealOrder: [],
    revealPhase: null,
    revealIndex: 0,
    currentRevealPlayerId: null,
    createdAt: serverTimestamp(),
    ...(song ? { scoringStartedAt: serverTimestamp() } : {}),
  });
  batch.update(doc(db, 'games', gameId), {
    activeRoundId: roundRef.id,
    roundCount: roundNumber,
  });

  await batch.commit();
  return roundRef.id;
}

export async function submitSong(gameId, roundId, song) {
  await updateDoc(doc(db, 'games', gameId, 'rounds', roundId), {
    song,
    phase: 'scoring',
    scoringStartedAt: serverTimestamp(),
  });
}

export async function endListening(gameId, roundId) {
  await updateDoc(doc(db, 'games', gameId, 'rounds', roundId), {
    phase: 'scoring',
    scoringStartedAt: serverTimestamp(),
  });
}

export async function lockInScore(gameId, roundId, userId, { actualScore, predictions }) {
  await setDoc(doc(db, 'games', gameId, 'rounds', roundId, 'scores', userId), {
    actualScore,
    predictions,
    lockedIn: true,
    submittedAt: serverTimestamp(),
  });
}

export async function unlockScore(gameId, roundId, userId) {
  await updateDoc(doc(db, 'games', gameId, 'rounds', roundId, 'scores', userId), {
    lockedIn: false,
    submittedAt: deleteField(),
  });
}

export async function sendPoke(gameId, roundId, pokerId, pokedId, text) {
  const key = `${pokerId}_${pokedId}`;
  await updateDoc(doc(db, 'games', gameId, 'rounds', roundId), {
    [`pokes.${key}`]: { text, sentAt: serverTimestamp() },
  });
}

export async function startRevealing(gameId, roundId, playerIds, chooserId) {
  // Predictions: chooser goes first
  const predRevealOrder = [chooserId, ...playerIds.filter((id) => id !== chooserId)];
  // Scores: chooser goes last
  const scoreRevealOrder = [...playerIds.filter((id) => id !== chooserId), chooserId];

  await updateDoc(doc(db, 'games', gameId, 'rounds', roundId), {
    phase: 'revealing',
    predRevealOrder,
    scoreRevealOrder,
    revealPhase: 'predictions',
    revealIndex: 0,
    // Tracks which target's prediction is currently being revealed (granular reveal).
    // Advances through the predictor's targets one at a time before moving to next predictor.
    predictionTargetIndex: 0,
    currentRevealPlayerId: predRevealOrder[0],
  });
}

/**
 * Advances the reveal state by one step.
 *
 * Predictions phase — granular: each predictor reveals one target at a time.
 *   predictionTargetIndex tracks which target is currently being revealed.
 *   Advancing increments predictionTargetIndex until all targets are done,
 *   then moves to the next predictor (incrementing revealIndex).
 *   When all predictors are done, transitions to the scores phase.
 *
 * Scores phase — one player at a time (unchanged from original).
 *
 * @param {string} gameId
 * @param {string} roundId
 * @param {object} round   — current round data from Firestore
 * @param {string[]} playerIds — game.playerIds (needed to compute prediction targets)
 */
export async function advanceReveal(gameId, roundId, round, playerIds) {
  const {
    predRevealOrder,
    scoreRevealOrder,
    revealPhase,
    revealIndex,
    predictionTargetIndex = 0,
  } = round;

  const roundRef = doc(db, 'games', gameId, 'rounds', roundId);

  if (revealPhase === 'predictions') {
    const currentPredictorUid = predRevealOrder[revealIndex];
    // Targets = all playerIds except the current predictor
    const targets = playerIds.filter((id) => id !== currentPredictorUid);
    const nextTargetIndex = predictionTargetIndex + 1;

    if (nextTargetIndex < targets.length) {
      // Reveal next target's prediction (same predictor)
      await updateDoc(roundRef, {
        predictionTargetIndex: nextTargetIndex,
      });
    } else {
      // All targets revealed for this predictor — move to next predictor
      const nextRevealIndex = revealIndex + 1;
      if (nextRevealIndex < predRevealOrder.length) {
        await updateDoc(roundRef, {
          revealIndex: nextRevealIndex,
          predictionTargetIndex: 0,
          currentRevealPlayerId: predRevealOrder[nextRevealIndex],
        });
      } else {
        // All predictors done — switch to scores phase
        await updateDoc(roundRef, {
          revealPhase: 'scores',
          revealIndex: 0,
          predictionTargetIndex: 0,
          currentRevealPlayerId: scoreRevealOrder[0],
        });
      }
    }
  } else {
    // revealPhase === 'scores'
    const nextIndex = revealIndex + 1;
    if (nextIndex < scoreRevealOrder.length) {
      await updateDoc(roundRef, {
        revealIndex: nextIndex,
        currentRevealPlayerId: scoreRevealOrder[nextIndex],
      });
    } else {
      // All scores revealed — round complete
      await updateDoc(roundRef, {
        phase: 'round_results',
        currentRevealPlayerId: null,
      });
    }
  }
}

export async function endGame(gameId) {
  await updateDoc(doc(db, 'games', gameId), {
    status: 'completed',
    completedAt: serverTimestamp(),
  });
}

export async function leaveGame(gameId, userId) {
  await updateDoc(doc(db, 'games', gameId), {
    playerIds: arrayRemove(userId),
    [`playerNames.${userId}`]: deleteField(),
  });
}

export function subscribeToGame(gameId, callback) {
  return onSnapshot(doc(db, 'games', gameId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
}

export function subscribeToRound(gameId, roundId, callback) {
  return onSnapshot(doc(db, 'games', gameId, 'rounds', roundId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
}

export function subscribeToScores(gameId, roundId, callback) {
  return onSnapshot(
    collection(db, 'games', gameId, 'rounds', roundId, 'scores'),
    (snap) => {
      const scores = {};
      snap.forEach((d) => { scores[d.id] = d.data(); });
      callback(scores);
    }
  );
}

export async function getRounds(gameId) {
  const snap = await getDocs(collection(db, 'games', gameId, 'rounds'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getGameById(gameId) {
  const snap = await getDoc(doc(db, 'games', gameId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// Finds an active or waiting game where the given user is a player.
// Used to auto-redirect a player who joins on a new device (e.g. mobile).
export async function findActiveGameForPlayer(userId) {
  const q = query(
    collection(db, 'games'),
    where('playerIds', 'array-contains', userId),
    where('status', 'in', ['active', 'waiting']),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function getAllScores(gameId) {
  const roundsSnap = await getDocs(collection(db, 'games', gameId, 'rounds'));
  const result = {};
  for (const roundDoc of roundsSnap.docs) {
    const scoresSnap = await getDocs(
      collection(db, 'games', gameId, 'rounds', roundDoc.id, 'scores')
    );
    result[roundDoc.id] = {};
    scoresSnap.forEach((d) => { result[roundDoc.id][d.id] = d.data(); });
  }
  return result;
}
